import { useState, useCallback } from 'react';
import { useAppDispatch } from '../../../../../../app/hooks';
import { updateApprovalAction as updateApproval } from '../../../../../../services/RefundService/slice';
import { ApprovalView } from '../../../../../../services/RefundService/types/refundTypes';
import {
  ApprovalAction,
  ACTION_TYPES,
  getInitialAction,
  createUpdatedApproval,
} from '../constants';

export interface FormState {
  action: ApprovalAction;
  comments: string;
  rejectionReason: string;
  isSubmitting: boolean;
}

export interface UseApprovalFormOptions {
  approval: ApprovalView;
  onClose: () => void;
}

export interface UseApprovalFormReturn {
  formState: FormState;
  updateFormState: (updates: Partial<FormState>) => void;
  handleSubmit: () => Promise<void>;
  handleClose: () => void;
  isValidSubmission: boolean;
}

export function useApprovalForm({
  approval,
  onClose,
}: UseApprovalFormOptions): UseApprovalFormReturn {
  const dispatch = useAppDispatch();

  const [formState, setFormState] = useState<FormState>({
    action: getInitialAction(approval),
    comments: approval.comments || '',
    rejectionReason: approval.rejectionReason || '',
    isSubmitting: false,
  });

  const updateFormState = useCallback((updates: Partial<FormState>) => {
    setFormState((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetForm = useCallback(() => {
    setFormState({
      action: ACTION_TYPES.APPROVE,
      comments: '',
      rejectionReason: '',
      isSubmitting: false,
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (formState.action === ACTION_TYPES.REJECT && !formState.rejectionReason.trim()) {
      return;
    }

    updateFormState({ isSubmitting: true });

    try {
      const updatedApproval = createUpdatedApproval(
        approval,
        formState.action,
        formState.comments,
        formState.rejectionReason
      );

      dispatch(
        updateApproval({
          level: approval.level,
          updates: updatedApproval,
        })
      );

      resetForm();
      onClose();
    } catch (error) {
      console.error('Approval action failed:', error);
    } finally {
      updateFormState({ isSubmitting: false });
    }
  }, [approval, formState, dispatch, onClose, resetForm, updateFormState]);

  const handleClose = useCallback(() => {
    if (!formState.isSubmitting) {
      resetForm();
      onClose();
    }
  }, [formState.isSubmitting, resetForm, onClose]);

  const isValidSubmission =
    formState.action === ACTION_TYPES.APPROVE ||
    (formState.action === ACTION_TYPES.REJECT && formState.rejectionReason.trim().length > 0);

  return {
    formState,
    updateFormState,
    handleSubmit,
    handleClose,
    isValidSubmission,
  };
}
